/**
 * NotificationSettings Component
 * 
 * Main component for managing notification preferences.
 * Allows users to configure email and in-app notification settings
 * for different resource types (claims, documents, tasks, etc.)
 */

'use client';

import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Bell, Mail, FileText, ListChecks, FileStack, StickyNote, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useNotificationSettings } from '@/hooks/useNotificationSettings';
import { Switch } from '@/components/ui/switch';
import type { NotificationSettingsSettingsInApp, NotificationSettingsSettingsEmail } from '@/api/generated/client';

type NotificationType = 'claim' | 'document' | 'comparison' | 'task' | 'supplement' | 'note' | 'email';

interface NotificationCategory {
  key: NotificationType;
  label: string;
  description: string;
  icon: React.ElementType;
}

const NOTIFICATION_CATEGORIES: NotificationCategory[] = [
  {
    key: 'claim',
    label: 'Claims',
    description: 'New claim creation, status changes',
    icon: AlertCircle,
  },
  {
    key: 'document',
    label: 'Documents',
    description: 'Document uploads',
    icon: FileText,
  },
  {
    key: 'task',
    label: 'Tasks',
    description: 'Manual task creation, task completions',
    icon: ListChecks,
  },
  {
    key: 'comparison',
    label: 'Comparisons',
    description: 'Comparison completion',
    icon: FileStack,
  },
  {
    key: 'supplement',
    label: 'Supplements',
    description: 'Supplement completion',
    icon: FileStack,
  },
  {
    key: 'note',
    label: 'Notes',
    description: 'New notes added to claims',
    icon: StickyNote,
  },
  {
    key: 'email',
    label: 'Emails',
    description: 'Emails sent from or received via Gmail',
    icon: Mail,
  },
];

export function NotificationSettings() {
  const { toast } = useToast();
  const { settings, loading, error, updateSettings } = useNotificationSettings();
  const [saving, setSaving] = useState(false);

  // Local state for form
  const [emailSettings, setEmailSettings] = useState<NotificationSettingsSettingsEmail>(
    settings?.settings.email || {}
  );
  const [inAppSettings, setInAppSettings] = useState<NotificationSettingsSettingsInApp>(
    settings?.settings.inApp || {}
  );

  // Update local state when settings load
  useEffect(() => {
    if (settings) {
      setEmailSettings(settings.settings.email);
      setInAppSettings(settings.settings.inApp);
    }
  }, [settings]);

  const handleEmailToggle = (key: NotificationType, enabled: boolean) => {
    setEmailSettings((prev) => ({
      ...prev,
      [key]: enabled,
    }));
  };

  const handleInAppToggle = (key: NotificationType, enabled: boolean) => {
    setInAppSettings((prev) => ({
      ...prev,
      [key]: enabled,
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings({
        settings: {
          email: emailSettings,
          inApp: inAppSettings,
        },
      });

      toast({
        title: 'Success',
        description: 'Notification settings saved successfully',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to save notification settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (settings) {
      setEmailSettings(settings.settings.email);
      setInAppSettings(settings.settings.inApp);
    }
  };

  const hasChanges = () => {
    if (!settings) return false;
    return (
      JSON.stringify(emailSettings) !== JSON.stringify(settings.settings.email) ||
      JSON.stringify(inAppSettings) !== JSON.stringify(settings.settings.inApp)
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-destructive">
            <p>Failed to load notification settings</p>
            <p className="text-sm text-muted-foreground mt-2">{error.message}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6 max-sm:mx-auto max-sm:max-w-7xl">
      <Card>
        <CardHeader>
          <CardTitle>Notification Settings</CardTitle>
          <CardDescription>
            Manage your notification preferences for email and in-app notifications
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Header Row */}
          <div className="grid grid-cols-[1fr_auto_auto] gap-4 border-b pb-2 max-sm:hidden">
            <div className="font-medium text-sm text-muted-foreground">Notification Type</div>
            <div className="flex items-center gap-2 font-medium text-sm text-muted-foreground w-24 justify-center">
              <Mail className="h-4 w-4" />
              <span>Email</span>
            </div>
            <div className="flex items-center gap-2 font-medium text-sm text-muted-foreground w-24 justify-center">
              <Bell className="h-4 w-4" />
              <span>In-App</span>
            </div>
          </div>

          {/* Notification Categories */}
          {NOTIFICATION_CATEGORIES.map((category) => {
            const Icon = category.icon;
            return (
              <div
                key={category.key}
                className="grid grid-cols-[1fr_auto_auto] gap-4 items-center py-3 border-b last:border-0 max-sm:grid-cols-1 max-sm:gap-3"
              >
                <div className="flex items-start gap-3">
                  <Icon className="h-5 w-5 mt-0.5 text-muted-foreground" />
                  <div>
                    <div className="font-medium">{category.label}</div>
                    <div className="text-sm text-muted-foreground">{category.description}</div>
                  </div>
                </div>
                <div className="flex items-center justify-center w-24 max-sm:justify-between max-sm:rounded-md max-sm:border max-sm:p-2 max-sm:w-auto">
                  <span className="text-sm text-muted-foreground sm:hidden">Email</span>
                  <Switch
                    checked={emailSettings[category.key] ?? true}
                    onCheckedChange={(checked) => handleEmailToggle(category.key, checked)}
                  />
                </div>
                <div className="flex items-center justify-center w-24 max-sm:justify-between max-sm:rounded-md max-sm:border max-sm:p-2 max-sm:w-auto">
                  <span className="text-sm text-muted-foreground sm:hidden">In-App</span>
                  <Switch
                    checked={inAppSettings[category.key] ?? true}
                    onCheckedChange={(checked) => handleInAppToggle(category.key, checked)}
                  />
                </div>
              </div>
            );
          })}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 max-sm:flex-col-reverse max-sm:gap-2">
            <Button
              variant="outline"
              onClick={handleReset}
              disabled={!hasChanges() || saving}
              className="max-sm:w-full"
            >
              Reset
            </Button>
            <Button
              onClick={handleSave}
              disabled={!hasChanges() || saving}
              className="max-sm:w-full"
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
